import { TripForDriverOutput } from '@allocab/allocab-driver-shared-types';
import { CircleDotSolid, ClockSolid, LocationDotSolid, RoadSolid } from '@allocab/allocab-icons';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { twMerge } from 'tailwind-merge';

import { queryKeys } from '../../services/datastore/queryKeys';
import { t } from '../../services/translation/translation';
import { formatDateForParisTZ } from '../../utils/date';
import { getStepForTrip, getTripStatus, shortenFormattedAddress } from '../../utils/trip';

type Props = {
  trip: TripForDriverOutput;
};

export const TripCalendarItem = ({ trip }: Props) => {
  const queryClient = useQueryClient();
  const step = getStepForTrip(trip);

  const handlePress = useCallback(() => {
    queryClient.setQueryData(queryKeys.user.fetchTrip(trip.key), trip);
  }, [queryClient, trip]);

  return (
    <div
      className={twMerge(
        'rounded-md px-8 p-2 text-white w-full text-xs flex flex-col gap-2',
        step === 'CANCELLED'
          ? 'bg-red-800'
          : step === 'DONE'
          ? 'bg-green-800'
          : step === 'ACCEPTED'
          ? 'bg-blue-800'
          : 'bg-[#C74F0A]',
      )}
      onClick={handlePress}
    >
      <p className="font-bold text-sm">
        {formatDateForParisTZ(trip.resume.departureAt, t('date.time'), false)}
        {' - '}
        {getTripStatus(trip)}
      </p>
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <CircleDotSolid width={12} height={12} className="fill-orange-400" />
          <p className="flex-1 min-w-0 text-ellipsis overflow-hidden">
            {shortenFormattedAddress(trip.resume.start.formattedAddress)}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <LocationDotSolid width={14} height={14} className="fill-current" />
          <p className="flex-1 min-w-0">{shortenFormattedAddress(trip.resume.end.formattedAddress)}</p>
        </div>
      </div>
      <div className="flex  xl:flex-col xl:space-y-2 2xl:space-y-0 2xl:flex-row space-x-2 xl:space-x-0 2xl:space-x-2">
        <div className="flex space-x-1">
          <ClockSolid width={14} height={14} className="fill-current place-self-center" />
          <div>~{t('common.x-min', { count: Math.ceil(trip.resume.duration / 60) })}</div>
        </div>
        <div className="flex space-x-1">
          <RoadSolid width={14} height={14} className="fill-current place-self-center" />
          <div>~{t('common.x-kms', { count: Math.ceil(trip.resume.distance / 1000) })}</div>
        </div>
      </div>
    </div>
  );
};
